import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import type { FullscreenOverlayPortalProps } from './types';
import { logError } from '@zg-rentals/log-error';

// Module-level scroll lock management (shared across all FullscreenOverlay instances)
// Prevents reference counting bug when multiple overlays are open simultaneously
let activeOverlayCount = 0;
let storedOriginalOverflow = '';
let savedScrollPosition = 0;
// Pre-open state for controlled overlays that bypass FullscreenOverlayTrigger.
// capturePreFocusScroll: call on pointerdown (before browser focuses the
//   element and auto-scrolls). The Portal reads this as a scroll fallback.
// captureOpener: call in the click handler to record the element that should
//   receive focus when the overlay closes (keyboard a11y).
let preFocusScrollY: number | null = null;
let preFocusOpener: HTMLElement | null = null;
let preFocusOpenedViaKeyboard = false;
export const capturePreFocusScroll = (): void => {
  preFocusScrollY = window.scrollY;
};
export const captureOpener = (element: HTMLElement | null, keyboard = false): void => {
  preFocusOpener = element;
  preFocusOpenedViaKeyboard = keyboard;
};

let pendingScrollRestoreRaf = 0;
const storedSiblingDisplays = new Map<HTMLElement, string>();

// Module-level overlay stack management for proper ESC key hierarchy
// Tracks overlay instances to ensure ESC only closes the topmost overlay
let overlayStack: Array<string> = [];

/**
 * Stack management utilities for overlay hierarchy
 * Used by FullscreenOverlayContent to implement proper ESC key behavior
 */
export const addToOverlayStack = (id: string): void => {
  overlayStack.push(id);
};

export const removeFromOverlayStack = (id: string): void => {
  overlayStack = overlayStack.filter((stackId) => stackId !== id);
};

export const isTopmostOverlay = (id: string): boolean => {
  return overlayStack.length > 0 && overlayStack[overlayStack.length - 1] === id;
};

export const hasActiveOverlays = (): boolean => {
  return overlayStack.length > 0;
};

/**
 * FullscreenOverlayPortal Component
 *
 * Renders the overlay content in a React portal outside the normal DOM hierarchy.
 * This ensures proper z-index stacking and prevents parent elements from interfering
 * with the overlay display.
 *
 * Also manages body overflow to prevent background scrolling when overlay is open.
 * Supports multiple overlays with reference counting - scroll is only unlocked when
 * the last overlay closes.
 *
 * @example
 * <FullscreenOverlay.Portal>
 *   <FullscreenOverlay.Backdrop>
 *     <FullscreenOverlay.Content>...</FullscreenOverlay.Content>
 *   </FullscreenOverlay.Backdrop>
 * </FullscreenOverlay.Portal>
 *
 * @example With custom container
 * <FullscreenOverlay.Portal containerId="custom-portal-root">
 *   ...
 * </FullscreenOverlay.Portal>
 */
export const FullscreenOverlayPortal = ({ children, containerId = 'portal-root' }: FullscreenOverlayPortalProps) => {
  const { isOpen, triggerRef } = useFullscreenOverlay();
  const [hasMounted, setHasMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const isNestedRef = useRef(false);
  const prevIsOpenRef = useRef(false);
  const localRafRef = useRef(0);

  // Capture nesting state when isOpen transitions to true (before effects increment activeOverlayCount).
  // Nested overlays on mobile need fixed positioning to appear on top of the document-flow parent overlay.
  if (isOpen && !prevIsOpenRef.current) {
    isNestedRef.current = activeOverlayCount > 0;
  }
  if (!isOpen) {
    isNestedRef.current = false;
  }
  prevIsOpenRef.current = isOpen;

  // SSR-safe mounting detection
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Cancel any pending scroll-restore rAF that THIS Portal scheduled when it
  // unmounts. Without this, an rAF scheduled by close-cleanup can outlive the
  // component and fire window.scrollTo on a stale Portal — harmless in
  // production but it pollutes module-level state across test runs.
  useEffect(() => {
    return () => {
      if (localRafRef.current) {
        cancelAnimationFrame(localRafRef.current);
        if (pendingScrollRestoreRaf === localRafRef.current) {
          pendingScrollRestoreRaf = 0;
        }
        localRafRef.current = 0;
      }
    };
  }, []);

  // Get or create portal container in effect (client-only)
  useEffect(() => {
    if (!hasMounted) return;

    let container = document.getElementById(containerId);

    if (!container) {
      // Create the container if it doesn't exist
      container = document.createElement('div');
      container.id = containerId;
      document.body.appendChild(container);
    }

    if (!container) {
      // Log error if we still can't get the container
      logError({
        error: new Error(`Failed to create portal container with id: ${containerId}`),
        context: {
          component: 'FullscreenOverlayPortal',
          containerId,
        },
      });
      return;
    }

    setPortalContainer(container);
  }, [containerId, hasMounted]);

  // Reference-counted scroll management
  // Mobile: hide page siblings so spoke scrolls at document level (triggers iOS Safari chrome retraction)
  // Desktop: lock body scroll (existing fixed overlay behavior)
  useEffect(() => {
    if (!hasMounted || typeof document === 'undefined') return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const previousCount = activeOverlayCount;

    if (isOpen) {
      activeOverlayCount++;
      if (previousCount === 0) {
        if (isMobile) {
          cancelAnimationFrame(pendingScrollRestoreRaf);
          pendingScrollRestoreRaf = 0;
          // Prefer scroll captured at the trigger point (before browser
          // focus auto-scroll). For controlled overlays that bypass Trigger,
          // fall back to preFocusScrollY (set via capturePreFocusScroll on
          // pointerdown), then window.scrollY as a last resort.
          savedScrollPosition = triggerRef.current.scrollY ?? preFocusScrollY ?? window.scrollY;
          preFocusScrollY = null;
          const portalEl = document.getElementById(containerId);
          const modalRoot = document.getElementById('modal-root');
          Array.from(document.body.children).forEach((el) => {
            if (
              el !== portalEl &&
              el !== modalRoot &&
              el instanceof HTMLElement &&
              !el.hasAttribute('data-confetti-canvas')
            ) {
              storedSiblingDisplays.set(el, el.style.display);
              el.style.display = 'none';
            }
          });
          window.scrollTo(0, 0);
        } else {
          storedOriginalOverflow = document.body.style.overflow || '';
          document.body.style.overflow = 'hidden';
        }
      } else if (isMobile) {
        // Nested overlay on mobile: lock body scroll so the parent overlay doesn't scroll behind the fixed wrapper
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      if (isOpen && hasMounted) {
        activeOverlayCount--;
        if (activeOverlayCount === 0) {
          if (isMobile) {
            storedSiblingDisplays.forEach((display, el) => {
              el.style.display = display;
            });
            storedSiblingDisplays.clear();
            document.body.style.overflow = '';
            // Capture now — a quick close-then-open could overwrite the
            // module-level savedScrollPosition before the rAF fires.
            const positionToRestore = savedScrollPosition;
            // Defer scroll restoration to allow the browser to reflow after
            // restoring siblings from display:none. Without this, mobile
            // browsers (especially iOS Safari) haven't recalculated the
            // scrollable height yet, so scrollTo gets clamped to 0.
            // Capture trigger info before the rAF — a quick reopen could overwrite it.
            const { element: triggerEl, keyboard: wasKeyboard } = triggerRef.current;
            const openerEl = triggerEl ?? preFocusOpener;
            const shouldRestoreFocus = wasKeyboard || preFocusOpenedViaKeyboard;
            pendingScrollRestoreRaf = requestAnimationFrame(() => {
              pendingScrollRestoreRaf = 0;
              localRafRef.current = 0;
              window.scrollTo(0, positionToRestore);
              // Only restore focus for keyboard opens — touch opens skip this
              // to avoid browser scroll-into-view fighting with scroll restore.
              if (shouldRestoreFocus && openerEl) {
                openerEl.focus({ preventScroll: true });
              }
              // Corrective second restore: mobile browser chrome (address bar
              // expand/collapse) and late layout passes can nudge scroll by a
              // few pixels after the first restore. A second frame catches it.
              requestAnimationFrame(() => {
                if (Math.abs(window.scrollY - positionToRestore) > 1) {
                  window.scrollTo(0, positionToRestore);
                }
              });
            });
            localRafRef.current = pendingScrollRestoreRaf;
            triggerRef.current = { element: null, keyboard: false, scrollY: null };
            preFocusOpener = null;
            preFocusOpenedViaKeyboard = false;
          } else {
            document.body.style.overflow = storedOriginalOverflow;
            storedOriginalOverflow = '';
            // Desktop: restore focus to trigger after scroll lock is released.
            const { element: triggerEl } = triggerRef.current;
            const openerEl = triggerEl ?? preFocusOpener;
            if (openerEl) {
              openerEl.focus({ preventScroll: true });
            }
            triggerRef.current = { element: null, keyboard: false, scrollY: null };
            preFocusOpener = null;
            preFocusOpenedViaKeyboard = false;
          }
        } else if (isMobile) {
          // Nested overlay closing, parent still open: restore scrolling for parent's document flow
          document.body.style.overflow = '';
        }
      }
    };
  }, [isOpen, hasMounted, containerId]);

  // Don't render anything if not mounted (SSR safety), overlay is closed, or container not ready
  if (!hasMounted || !isOpen || !portalContainer) {
    return null;
  }

  // Nested overlays need fixed positioning to appear on top of the document-flow parent overlay on mobile
  if (isNestedRef.current) {
    return createPortal(
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 600, overflow: 'auto' }}>
        {children}
      </div>,
      portalContainer,
    );
  }

  return createPortal(children, portalContainer);
};
